// Iter 599/602/610 — Dashboard Kompetisi Operator Bordir: satu Leaderboard gabungan (Offline + Shopee), filter shift.
// Iter 610 — disederhanakan untuk operator: ringkasan kecil di halaman kerja, dashboard lengkap di panel terpisah.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { createPortal } from "react-dom";
import { TitikInputSheet } from "./bordir_titik_form";
import { IncompleteSheet } from "./bordir_incomplete_sheet";
import { ClaimsPendingPanel } from "./bordir_claims";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, LabelList } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_MS = 180_000;
const MAIN_PRESETS = [["today", "Hari Ini"], ["7d", "7 Hari"], ["30d", "30 Hari"]];
const MORE_PRESETS = [["yesterday", "Kemarin"], ["custom", "Pilih Tanggal"]];
const PRESET_LABEL = Object.fromEntries([...MAIN_PRESETS, ...MORE_PRESETS]);
const SHIFTS = [["all", "Semua Shift"], ["siang", "Siang"], ["malam", "Malam"]];
const SHIFT_SHORT = { all: "Semua Shift", siang: "Shift Siang", malam: "Shift Malam" };
const SHIFT_LABEL = { siang: "Siang", malam: "Malam", tidak_tercatat: "Shift tidak tercatat" };
const SRC_LABEL = { offline: "Offline", shopee: "Shopee", legacy: "Offline (riwayat lama)" };
const pctText = (v) => (v === null || v === undefined ? "-" : `${v}%`);
const unknownNote = (n) => n > 0 ? `${n} pekerjaan: Pcs belum tercatat` : "";
const SCHED = { siang: "Shift Siang", malam: "Shift Malam", libur: "Libur", tanpa_jadwal: "Tanpa Jadwal" };
const unitOf = () => "satuan titik";  // Iter 618 — kompetisi berdasarkan satuan titik terverifikasi
const fmt = (iso) => { try { return new Date(iso).toLocaleString("id-ID", { timeZone: "Asia/Jakarta", dateStyle: "medium", timeStyle: "short" }); } catch { return iso || "-"; } };
const clock = (d) => d ? d.toLocaleTimeString("id-ID", { timeZone: "Asia/Jakarta", hour: "2-digit", minute: "2-digit" }) : "-";
const short = (d) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;

function Chips({ items, value, onChange, tid }) {
  return <div role="tablist" className="opb-chips opb-chips-fit" data-testid={tid}>
    {items.map(([v, l]) => <button key={v} type="button" role="tab" aria-selected={value === v}
      className={`lb-chip sm${value === v ? " active" : ""}`} onClick={() => onChange(v)} data-testid={`${tid}-${v}`}>{l}</button>)}
  </div>;
}

const pendTotal = (l) => (l || []).reduce((n, x) => n + x.count, 0);

function Pending({ list, tid }) {
  if (!pendTotal(list)) return null;
  return <div className="warn-box small" data-testid={`${tid}-shopee-not-counted`}>
    {pendTotal(list)} pekerjaan Shopee yang Anda selesaikan belum bisa dihitung: {list.map(x => `${x.label} (${x.count})`).join("; ")}. Ini bukan berarti hasil Anda nol — hubungi Owner/Admin.
  </div>;
}

// Iter 642 — papan hasil sederhana: satu kartu ringkas + peringkat ringkas. Rumus tidak berubah.
// Target naik = selisih ke operator tepat di atas + 1 (melampaui); peringkat tujuan = peringkat operator itu (aturan seri).
const target = (rows, r) => {
  if (!r || r.rank === 1) return null;
  const above = rows.filter(x => x.points > r.points).sort((x, y) => x.points - y.points)[0];
  return { add: r.gap_to_above + 1, rank: above ? above.rank : r.rank - 1 };
};
const incomplete = (k) => k && (k.titik_pcs < k.pcs || k.pcs_unknown_jobs > 0);

function Summary({ data, tid }) {
  const k = data.kpi || {}, rows = data.leaderboard, who = data.me || data.operator_id;
  const mine = rows.find(r => r.operator_id === who), t = target(rows, mine);
  return <div className="opb-card opb-summary" data-testid={`${tid}-kpi`}>
    {!data.has_own_data ? <div className="lb-row-sub" data-testid={`${tid}-own-empty`}>Belum ada hasil tercatat pada periode ini.</div> : <>
      <div className="opb-sum-main">
        <div><span className="opb-sum-label">Hasil {data.me ? "Saya" : ""}</span>
          <b className="opb-big" data-testid={`${tid}-kpi-points-value`}>{k.points_events ? k.points : "—"}</b><span className="opb-sum-unit">satuan titik</span></div>
        <div data-testid={`${tid}-myrank`}><span className="opb-sum-label">Peringkat</span>
          <b className="opb-big" data-testid={`${tid}-myrank-value`}>{mine ? `#${mine.rank}` : "—"}</b></div>
      </div>
      <div className="opb-sum-target" data-testid={`${tid}-myrank-gap`}>{!mine ? "Belum masuk peringkat — titik belum tercatat."
        : t ? <>Tambah <b>{t.add} satuan titik</b> untuk naik ke peringkat {t.rank}.</> : <b>Anda di peringkat 1!</b>}</div>
      <div className="lb-row-sub" data-testid={`${tid}-kpi-jobs-value`}>{k.tasks_done || 0} pekerjaan selesai · <span data-testid={`${tid}-kpi-pcs-value`}>{k.pcs || 0}</span> pcs</div>
      {(incomplete(k) || pendTotal(data.my_shopee_not_counted) > 0) && <span className="opb-badge opb-badge-warn" data-testid={`${tid}-kpi-titik-incomplete`}>Data belum lengkap — lihat Info Data</span>}
    </>}
  </div>;
}

function Board({ data, metric, tid, filters }) {
  const [fixOp, setFixOp] = useState(null);
  return <div className="opb-card" data-testid={`${tid}-leaderboard`}>
    <div className="opb-card-title">Peringkat <span className="lb-row-sub" data-testid={`${tid}-leaderboard-count`}>· {data.leaderboard.length} operator tercatat</span></div>
    {data.leaderboard.length === 0 && <div className="lb-row-sub" data-testid={`${tid}-leaderboard-empty`}>Belum ada hasil tercatat pada periode ini.</div>}
    {data.leaderboard.map(r => <div key={r.operator_id} className={`opb-lrow${r.rank === 1 ? " top" : ""}${r.operator_id === data.me ? " me" : ""}`} data-testid={`${tid}-leaderboard-row-${r.operator_id}`}>
      <span className="opb-lrank" data-testid={`${tid}-leaderboard-rank-${r.operator_id}`}>#{r.rank}</span>
      <span className="opb-lname">
        <span className="lb-row-name opb-lname-top" title={r.name}><span className="opb-lname-text">{r.name}</span>{r.operator_id === data.me && <em className="opb-you" data-testid={`${tid}-you`}>Anda</em>}</span>
        <span className="opb-mini" data-testid={`${tid}-leaderboard-jobs-${r.operator_id}`}>{r.tasks_done} pekerjaan · {r.pcs} pcs</span>
        {(r.titik_incomplete || r.pcs_incomplete) && (data.can_choose_operator
          ? <button type="button" className="opb-badge opb-badge-warn opb-badge-btn" onClick={() => setFixOp(r.operator_id)} data-testid={`${tid}-leaderboard-incomplete-${r.operator_id}`}>Data belum lengkap ›</button>
          : <em className="opb-badge opb-badge-warn" data-testid={`${tid}-leaderboard-incomplete-${r.operator_id}`}>Data belum lengkap</em>)}
      </span>
      <b className="lb-total" data-testid={`${tid}-leaderboard-value-${r.operator_id}`}>{r[metric]}<span className="lb-total-unit"> titik</span></b>
    </div>)}
    {fixOp && <IncompleteSheet operatorId={fixOp} filters={filters} onClose={() => setFixOp(null)} tid={`${tid}-incomplete`}/>}
  </div>;
}

const TIP = { contentStyle: { background: "#0f172a", border: "none", borderRadius: 8, color: "#f8fafc", fontSize: 12 },
  itemStyle: { color: "#f8fafc" }, labelStyle: { color: "#f8fafc", fontWeight: 700 }, cursor: { fill: "rgba(148,163,184,.15)" } };

function CompareChart({ data, metric, tid }) {
  const rows = data.leaderboard.map(r => ({ name: r.name.length > 14 ? `${r.name.slice(0, 13)}…` : r.name, v: r[metric] }));
  if (!rows.length) return null;
  return <div className="opb-card" data-testid={`${tid}-compare-chart`}>
    <div className="opb-card-title">Perbandingan Hasil ({unitOf(metric)})</div>
    <ResponsiveContainer width="100%" height={Math.max(90, rows.length * 38 + 20)}>
      <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 40, left: 0, bottom: 4 }}>
        <XAxis type="number" hide allowDecimals={false}/>
        <YAxis type="category" dataKey="name" width={96} tick={{ fontSize: 12, fill: "var(--lb-text, #0f172a)" }}/>
        <Tooltip {...TIP} formatter={(v) => [`${v} ${unitOf(metric)}`, "Hasil"]}/>
        <Bar dataKey="v" fill="#0f766e" radius={[0, 4, 4, 0]} barSize={22} isAnimationActive={false}>
          <LabelList dataKey="v" position="right" style={{ fontSize: 12, fontWeight: 700, fill: "var(--lb-text, #0f172a)" }}/>
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  </div>;
}

function DailyChart({ data, metric, tid }) {
  const d = data.daily || [];
  const rows = d.map(x => ({ label: short(x.date), Siang: x[`siang_${metric}`], Malam: x[`malam_${metric}`], "Shift tidak tercatat": x[`lain_${metric}`] }));
  const total = rows.reduce((n, x) => n + x.Siang + x.Malam + x["Shift tidak tercatat"], 0);
  const lain = rows.some(x => x["Shift tidak tercatat"] > 0);
  return <div className="opb-card" data-testid={`${tid}-daily-chart`}>
    <div className="opb-card-title">Perkembangan Harian {data.me ? "Saya" : ""} ({unitOf(metric)})</div>
    {d.length < 2 ? <div className="lb-row-sub" data-testid={`${tid}-daily-hint`}>Pilih 7 Hari atau 30 Hari untuk melihat perkembangan harian.</div>
      : total === 0 ? <div className="lb-row-sub" data-testid={`${tid}-daily-empty`}>Belum ada hasil tercatat pada periode ini.</div>
      : <ResponsiveContainer width="100%" height={170}>
        <BarChart data={rows} margin={{ top: 6, right: 4, left: -24, bottom: 0 }}>
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: "var(--lb-text, #0f172a)" }} interval={d.length > 10 ? 2 : 0}/>
          <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "var(--lb-text, #0f172a)" }}/>
          <Tooltip {...TIP}/>
          <Legend wrapperStyle={{ fontSize: 11 }}/>
          <Bar dataKey="Siang" stackId="s" fill="#f59e0b" isAnimationActive={false}/>
          <Bar dataKey="Malam" stackId="s" fill="#4f46e5" isAnimationActive={false}/>
          {lain && <Bar dataKey="Shift tidak tercatat" stackId="s" fill="#94a3b8" isAnimationActive={false}/>}
        </BarChart>
      </ResponsiveContainer>}
  </div>;
}

const needsTitik = (e) => (e.source === "shopee" || e.source === "legacy") && (e.pcs === null || e.titik_status !== "lengkap");

const dShort = (iso) => { try { const d = new Date(iso); return `${d.toLocaleDateString("id-ID", { timeZone: "Asia/Jakarta", day: "numeric", month: "short", year: "numeric" })} · ${d.toLocaleTimeString("id-ID", { timeZone: "Asia/Jakarta", hour: "2-digit", minute: "2-digit" })}`; } catch { return iso || "-"; } };
const srcText = (e) => e.source === "shopee" ? (e.marketplace === "Shopee" ? "Shopee" : "Pesanan online (sumber belum dipastikan)") : "Offline";

function History({ h, page, setPage, tid, me, onTitik }) {
  return <div className="opb-card" data-testid={`${tid}-history`}>
    <div className="opb-card-title">Riwayat Pekerjaan ({h.total})</div>
    {h.items.length === 0 && <div className="lb-row-sub">Belum ada riwayat pada periode ini.</div>}
    {h.items.map((e, i) => <details key={`${e.task_id}-${e.at}-${i}`} className="opb-hrow" data-testid={`${tid}-history-row-${i}`}>
      <summary className="opb-hsum">
        <span className="opb-hmain"><b>{e.is_rework ? "Perbaikan" : e.task_done ? "Selesai" : "Progres"}</b>{` · ${e.pcs === null ? "pcs belum tercatat" : `${e.pcs} pakaian`} · ${e.points !== null ? `${e.points} satuan titik` : "titik belum tercatat"}${e.points !== null && e.pcs !== null && (e.titik_pcs || 0) < e.pcs ? ` · ${e.pcs - (e.titik_pcs || 0)} pcs belum bertitik` : ""}`}
          {(e.titik_status === "sebagian" || e.pcs === null || (e.pcs !== null && e.points === null)) && <em className="opb-badge opb-badge-warn">Belum lengkap</em>}
          {e.corrected && <em className="opb-badge">Dikoreksi</em>}</span>
        <span className="opb-mini">{dShort(e.at)}</span>
      </summary>
      <span className="opb-mini" data-testid={`${tid}-history-detail-${i}`}>No. pesanan {e.order_number || "-"} · {srcText(e)} · {SHIFT_LABEL[e.shift] || e.shift}{e.shift_source === "koreksi" ? " (dikoreksi)" : ""}{e.operator_name ? ` · ${e.operator_name}` : ""}{e.manual ? " · titik diisi manual" : ""}</span>
      {me && e.operator_id === me && (needsTitik(e) || e.manual) && <button type="button" className="soft-btn small-btn opb-fix-btn" onClick={() => onTitik(e)}
        data-testid={`${tid}-history-titik-${i}`}>{needsTitik(e) ? "Lengkapi Titik" : "Koreksi Titik"}</button>}
    </details>)}
    {h.pages > 1 && <div className="opb-pager">
      <button type="button" className="soft-btn small-btn" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid={`${tid}-history-prev`}>Sebelumnya</button>
      <span className="lb-row-sub">{page} / {h.pages}</span>
      <button type="button" className="soft-btn small-btn" disabled={page >= h.pages} onClick={() => setPage(page + 1)} data-testid={`${tid}-history-next`}>Berikutnya</button>
    </div>}
  </div>;
}

// Iter 617 — Owner/Admin: jejak input & koreksi titik oleh operator.
function TitikLog({ tid }) {
  const [rows, setRows] = useState(null);
  const [all, setAll] = useState(false);
  useEffect(() => {
    axios.get(`${API}/bordir-tasks/titik-manual/log`, { params: { only_corrected: all ? 0 : 1, limit: 50 }, withCredentials: true })
      .then(r => setRows(r.data.items)).catch(() => setRows([]));
  }, [all]);
  const show = (x) => x.lines?.length ? x.lines.map(l => `${l.pcs}×${l.titik}`).join(" + ") : x.titik ? `${x.titik} titik` : Object.values(x.items || {}).join(", ") + " titik";
  return <details className="opb-card" data-testid={`${tid}-titik-log`}>
    <summary className="lb-row-sub"><b>Riwayat Input & Koreksi Titik Operator</b></summary>
    <label className="lb-row-sub"><input type="checkbox" checked={all} onChange={e => setAll(e.target.checked)} data-testid={`${tid}-titik-log-all`}/> Tampilkan semua input (bukan hanya koreksi)</label>
    {rows === null ? <div className="lb-row-sub">Memuat…</div> : rows.length === 0 ? <div className="lb-row-sub" data-testid={`${tid}-titik-log-empty`}>Belum ada.</div>
      : rows.map(r => <div key={`${r.kind}-${r.ref_id}`} className="opb-hrow" data-testid={`${tid}-titik-log-row`}>
        <span><b>{r.order_number}</b> · {r.operator_name}{r.corrected ? " · Dikoreksi" : ""}</span>
        {(r.history || []).map((h, i) => <span key={i} className="lb-row-sub">{fmt(h.at)} · {h.by_name}: {i === 0 && !h.correction ? "isi" : `${show(h.before)} → `}{show(h.after)}{h.reason ? ` · alasan: ${h.reason}` : ""}</span>)}
      </div>)}
  </details>;
}

// Rincian teknis — tertutup secara default.
function CalcInfo({ data, tid }) {
  const c = data.coverage, s = c.shopee, k = data.kpi || {};
  return <details className="opb-card opb-cov" data-testid={`${tid}-calc-info`}>
    <summary className="lb-row-sub"><b>Info Data</b></summary>
    <Pending list={data.my_shopee_not_counted} tid={tid}/>
    <ul className="lb-row-sub opb-cov-list" data-testid={`${tid}-coverage`}>
      {data.has_own_data && incomplete(k) && <li data-testid={`${tid}-info-own-incomplete`}>Data Saya belum lengkap: titik belum tercatat untuk sebagian pekerjaan{k.pcs_unknown_jobs > 0 ? ` (${unknownNote(k.pcs_unknown_jobs)})` : ""}. Lengkapi lewat Riwayat Pekerjaan.</li>}
      {data.has_own_data && k.rework_pcs > 0 && <li>Perbaikan {k.rework_pcs} pcs (dihitung terpisah, tidak masuk peringkat)</li>}
      <li data-testid={`${tid}-basis-note`}>Peringkat berdasarkan total satuan titik yang sudah tercatat, bukan pcs.</li>
      {data.no_points_operators.length > 0 && <li data-testid={`${tid}-leaderboard-no-points`}>Titik belum tercatat (belum diperingkat): {data.no_points_operators.map(o => `${o.name} (${o.tasks_done} pekerjaan)`).join(", ")}</li>}
      {data.no_data_operators.length > 0 && <li data-testid={`${tid}-leaderboard-no-data`}>Belum ada hasil pada periode ini: {data.no_data_operators.map(o => o.name).join(", ")}</li>}
      <li>Hasil = jumlah pcs yang benar-benar dicatat selesai dikerjakan (Offline: dari progres; Shopee: dari jumlah barang pesanan yang diselesaikan). Claim, pembagian tugas, serah-terima, dan alihkan sisa tidak dihitung.</li>
      <li>Pcs dikerjakan ≠ jumlah pakaian: satu pakaian bisa dikerjakan beberapa operator pada posisi berbeda.</li>
      <li data-testid={`${tid}-cov-titik`}>Satuan titik = qty pakaian Bordir × titik per pakaian (Shopee: dari variasi pesanan, cadangan judul produk bila jelas satu angka; tidak ditebak; nota berisi beberapa pekerjaan tidak dibagi). Kelengkapan titik: Shopee {pctText(c.titik.shopee.pct)} · Offline {pctText(c.titik.offline.pct)} · Gabungan {pctText(c.titik.gabungan.pct)} dari pcs terverifikasi.</li>
      <li data-testid={`${tid}-cov-offline`}>Offline: {c.offline_multi.events} catatan progres · {c.offline_multi.pcs} pcs · {c.offline_multi.points} titik</li>
      <li data-testid={`${tid}-cov-shopee`}>Shopee: {s.eligible_jobs} pekerjaan selesai · {s.verified_jobs} dengan pcs terverifikasi ({s.eligible_pcs} pcs). Biaya setting/tambahan harga/ongkir dan item non-Bordir (mis. DTF) tidak dihitung sebagai pcs.</li>
      {s.pcs_unknown.map(x => <li key={x.reason} data-testid={`${tid}-cov-pcs-unknown-${x.reason}`}>Pcs belum tercatat — {x.label}: {x.count} pekerjaan (tetap dihitung sebagai pekerjaan selesai)</li>)}
      {s.excluded.map(x => <li key={x.reason} data-testid={`${tid}-cov-excluded-${x.reason}`}>Shopee belum dihitung — {x.label}: {x.count}</li>)}
      {c.legacy.entries > 0 && <li data-testid={`${tid}-cov-legacy`}>Riwayat Offline lama: {c.legacy.entries} pekerjaan · {c.legacy.pcs} pcs · {c.legacy.points} titik (hanya Semua Shift, "Shift tidak tercatat")</li>}
      {c.legacy.designer > 0 && <li data-testid={`${tid}-cov-legacy-designer`}>Riwayat lama oleh Desainer Bordir tidak masuk Leaderboard: {c.legacy.designer} item</li>}
      {c.unshifted_events > 0 && <li data-testid={`${tid}-cov-unshifted`}>Catatan lama "Shift tidak tercatat": {c.unshifted_events} (hanya dihitung di Semua Shift)</li>}
      <li>Titik Shopee dibaca dari variasi pesanan (hanya hasil pasti). Tidak ada peringkat kecepatan per jam. Peringkat bukan dasar perhitungan gaji.</li>
    </ul>
  </details>;
}

function MoreFilters({ preset, setPreset, from, setFrom, to, setTo, metric, setMetric, data, opId, setOpId, tid }) {
  return <details className="opb-more" data-testid={`${tid}-more-filters`} open={preset === "yesterday" || preset === "custom" || Boolean(opId)}>
    <summary className="lb-row-sub">Filter Lainnya</summary>
    <Chips items={MORE_PRESETS} value={preset} onChange={setPreset} tid={`${tid}-period-more`}/>
    {preset === "custom" && <div className="opb-dates">
      <input type="date" value={from} onChange={e => setFrom(e.target.value)} data-testid={`${tid}-from`}/>
      <span className="lb-row-sub">s/d</span>
      <input type="date" value={to} onChange={e => setTo(e.target.value)} data-testid={`${tid}-to`}/>
    </div>}
    {data?.can_choose_operator && <select value={opId} onChange={e => setOpId(e.target.value)} data-testid={`${tid}-operator`} className="opb-select">
      <option value="">Semua operator</option>
      {data.operators.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
    </select>}
  </details>;
}

function usePerformance(params, ready) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [at, setAt] = useState(null);
  const last = useRef(0);
  const load = useCallback((fresh) => {
    if (!ready) return;
    last.current = Date.now();
    axios.get(`${API}/bordir-tasks/performance`, { params: { ...params, ...(fresh ? { fresh: 1 } : {}) }, withCredentials: true, timeout: 20_000 })
      .then(r => { setData(r.data); setErr(""); setAt(new Date()); })
      .catch(e => setErr(e?.response?.data?.detail || e.message || "Gagal memuat performa"));
  }, [params, ready]);
  useEffect(() => { load(false); }, [load]);
  useEffect(() => {
    const tick = () => { if (document.visibilityState === "visible" && Date.now() - last.current >= REFRESH_MS - 1000) load(false); };
    const iv = setInterval(tick, REFRESH_MS);
    const saved = () => load(true);
    document.addEventListener("visibilitychange", tick);
    window.addEventListener("bordir-progress-saved", saved);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", tick); window.removeEventListener("bordir-progress-saved", saved); };
  }, [load]);
  return { data, err, at };
}

function useMySchedule(enabled) {
  const [s, setS] = useState(null);
  useEffect(() => {
    if (!enabled) return;
    axios.get(`${API}/bordir-tasks/my-schedule`, { withCredentials: true, timeout: 15_000 }).then(r => setS(r.data)).catch(() => setS(null));
  }, [enabled]);
  return s;
}

const schedText = (s) => !s ? "" : s.has_schedule
  ? `Jadwal hari ini: ${SCHED[s.today]}${s.today_window ? ` ${s.today_window.join("–")}` : ""} · minggu ini ${SCHED[s.this_week]} · minggu depan ${SCHED[s.next_week]}`
  : "Jadwal: Tanpa Jadwal (regu belum ditentukan) — tetap bisa mencatat pekerjaan";

// Ringkasan kecil di halaman kerja (Offline maupun Shopee).
function CompetitionBar({ data, err, metric, preset, shift, onOpen, tid }) {
  const rows = data?.leaderboard || [], u = unitOf(metric);
  const mine = rows.find(r => r.operator_id === data?.me), top = rows[0];
  return <div className="opb-bar" data-testid={`${tid}-bar`}>
    <div className="opb-bar-top">
      <b className="opb-bar-title">Performa Bordir</b>
      <span className="lb-row-sub" data-testid={`${tid}-bar-filter`}>{PRESET_LABEL[preset]} · {SHIFT_SHORT[shift]} · Gabungan Shopee & Offline</span>
    </div>
    {!data ? <div className="lb-row-sub">{err ? "Gagal memuat — coba lagi nanti" : "Memuat…"}</div> : <>
      <div className="opb-bar-stats">
        <div className="opb-bar-box"><span className="lb-row-sub">Hasil Saya</span><b className="opb-bar-num" data-testid={`${tid}-bar-output`}>{data.has_own_data ? (data.kpi?.points_events ? <>{data.kpi.points} <small>{u} · {data.kpi?.tasks_done || 0} pekerjaan</small></>
          : <small>Titik Belum Tercatat · {data.kpi?.tasks_done || 0} pekerjaan</small>) : "—"}</b></div>
        <div className="opb-bar-box"><span className="lb-row-sub">Peringkat Saya</span><b className="opb-bar-num" data-testid={`${tid}-bar-rank`}>{mine ? <>#{mine.rank}<small> / {rows.length}</small></> : "—"}</b></div>
      </div>
      <div className="opb-bar-note" data-testid={`${tid}-bar-leader`}>{!data.has_own_data
        ? (pendTotal(data.my_shopee_not_counted) ? `${pendTotal(data.my_shopee_not_counted)} pekerjaan Shopee belum dihitung titiknya.` : `Belum ada hasil tercatat ${String(PRESET_LABEL[preset] || "").toLowerCase()}.`)
        : !top ? "Belum ada hasil tercatat." : mine && target(rows, mine)
        ? <>Tambah <b>{target(rows, mine).add} {u}</b> untuk naik ke peringkat {target(rows, mine).rank}.</> : mine ? <b>Anda di peringkat 1!</b> : <>Peringkat 1: <b>{top.name}</b> ({top[metric]} {u})</>}</div>
    </>}
    <button type="button" className="primary opb-bar-btn" onClick={onOpen} data-testid={`${tid}-toggle`}>Lihat Dashboard</button>
  </div>;
}

function Content(p) {
  const { data, err, at, metric, tid, sched } = p;
  return <>
    <div className="opb-head">
      <span className={`lb-row-sub${err ? " opb-stale" : ""}`} data-testid={`${tid}-updated`}>
        {err ? `Gagal memperbarui — data terakhir pukul ${clock(at)} (bukan data terbaru)` : `Diperbarui ${clock(at)} WIB · otomatis tiap 3 menit`}</span>
      {sched && <span className="opb-mini" data-testid={`${tid}-schedule`}>{schedText(sched)}</span>}
    </div>
    <Chips items={MAIN_PRESETS} value={p.preset} onChange={p.setPreset} tid={`${tid}-period`}/>
    <Chips items={SHIFTS} value={p.shift} onChange={p.setShift} tid={`${tid}-shift`}/>
    <MoreFilters {...p}/>
    {err && <div className="warn-box small" data-testid={`${tid}-error`}>{err}</div>}
    {!data && !err && <div className="lb-row-sub">Memuat…</div>}
    {data && <div className={err ? "opb-dim" : ""}>
      {(data.me || data.operator_id) && <Summary data={data} tid={tid}/>}
      <Board data={data} metric={metric} tid={tid} filters={{ preset: p.preset, shift: p.shift, ...(p.preset === "custom" ? { date_from: p.from, date_to: p.to } : {}) }}/>
      <details className="opb-card opb-charts" data-testid={`${tid}-charts`}>
        <summary className="lb-row-sub" data-testid={`${tid}-charts-toggle`}><b>Lihat Grafik</b></summary>
        <CompareChart data={data} metric={metric} tid={tid}/>
        <DailyChart data={data} metric={metric} tid={tid}/>
      </details>
      <History h={data.history} page={p.page} setPage={p.setPage} tid={tid} me={data.me} onTitik={p.setTitikEv}/>
      {p.titikEv && <TitikInputSheet kind={p.titikEv.source} refId={p.titikEv.source === "shopee" ? p.titikEv.task_id : p.titikEv.ev_id}
        onClose={() => p.setTitikEv(null)} tid={`${tid}-titik`}/>}
      <CalcInfo data={data} tid={tid}/>
      {data.can_choose_operator && <TitikLog tid={tid}/>}
    </div>}
  </>;
}

export function OperatorBordirPerformance({ testIdPrefix = "opbordir-performa", compact = false }) {
  const tid = testIdPrefix;
  const [preset, setPreset] = useState("today");
  const [shift, setShift] = useState("all");
  const [opId, setOpId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const [metric, setMetric] = useState("points");
  const [open, setOpen] = useState(false);
  const [titikEv, setTitikEv] = useState(null);
  const ready = preset !== "custom" || Boolean(from && to);
  const params = useMemo(() => ({ preset, shift, operator_id: opId, page, rank_by: metric, ...(preset === "custom" ? { date_from: from, date_to: to } : {}) }), [preset, shift, opId, page, from, to, metric]);
  useEffect(() => { setPage(1); }, [preset, shift, opId, from, to]);
  const { data, err, at } = usePerformance(params, ready);
  const sched = useMySchedule(Boolean(data?.me));
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);
  const props = { data, err, at, metric, tid, sched, preset, setPreset, shift, setShift, from, setFrom, to, setTo, setMetric, opId, setOpId, page, setPage, titikEv, setTitikEv };
  if (!compact) return <section className="leaderboard-bordir opb" data-testid={tid}>
    <h3 className="lb-title">Performa Bordir</h3>
    <ClaimsPendingPanel/>
    <Content {...props}/>
  </section>;
  return <section className="leaderboard-bordir opb" data-testid={tid}>
    <CompetitionBar data={data} err={err} metric={metric} preset={preset} shift={shift} onOpen={() => setOpen(true)} tid={tid}/>
    {open && createPortal(<div className="leaderboard-bordir opb-sheet" role="dialog" aria-modal="true" aria-label="Dashboard Performa Bordir" data-testid={`${tid}-sheet`}>
      <div className="opb-sheet-head">
        <b>Dashboard Performa Bordir</b>
        <button type="button" className="soft-btn small-btn" onClick={() => setOpen(false)} data-testid={`${tid}-close`}>Tutup</button>
      </div>
      <div className="opb-sheet-body opb"><Content {...props}/></div>
    </div>, document.body)}
  </section>;
}

export default OperatorBordirPerformance;
